import {
    getUnscheduledInvoiceAmount,
    ScheduleResolutionStep,
} from "@common/helpers/domains/accounting/scheduleMatchConflicts";
import { currencyFormatter } from "@common/helpers/currency";
import { pluralizeWithCount } from "@common/helpers/string";
import { ScheduleMatchResolutionKind } from "@common/types/domains/accounting/schedule";
import { formatDateOnly } from "@helpers/date";

export interface ScheduleConflictOptionView {
    kind: ScheduleMatchResolutionKind;
    title: string;
    description: string;
    warning: string | null;
    isDisabled: boolean;
}

export interface ScheduleConflictStepView {
    title: string;
    body: string;
    extra: string | null;
    options: ScheduleConflictOptionView[];
    showMatchOneAtATime: boolean;
}

const BODY =
    "Matching keeps one set and removes the other. Choose which schedules to keep, or don't match them.";
const SINGLE_PAIR_TITLE =
    "Both this invoice and this transaction already have schedules";
const OTHER_MATCHES_GO_THROUGH = "Other selected matches still go through.";
const UNMATCH_DESCRIPTION = "Each keeps its own schedules.";

function earliest(dates: string[]): string {
    return [...dates].sort()[0];
}

function startsLabel(termStarts: string[]): string {
    return `Recognition starts ${formatDateOnly(earliest(termStarts), "MMM d, yyyy")}`;
}

function scheduleLabel(names: string[]): string {
    return names.length === 1 ? `the ${names[0]} schedule` : "the schedules";
}

/**
 * A scheduled transaction cannot be split, so when the match has to split a
 * transaction its schedule is not one the user can keep.
 */
function keepTransactionsWarning(options: {
    splitTransactionCount: number;
    unscheduledAmount: number;
}): string | null {
    if (options.splitTransactionCount > 0) {
        const subject =
            options.splitTransactionCount === 1
                ? "the transaction"
                : pluralizeWithCount(
                      "transaction",
                      options.splitTransactionCount,
                  );
        return `Matching would split ${subject}, and a scheduled transaction can't be split.`;
    }
    if (options.unscheduledAmount > 0) {
        return `${currencyFormatter.format(options.unscheduledAmount)} of this invoice will have no schedule.`;
    }
    return null;
}

/**
 * One wizard step: the invoice still in conflict with the transactions it
 * would be matched to. `isSinglePair` is fixed when the wizard opens so a
 * save of exactly one link keeps the 1:1 modal copy even after recompute.
 */
export function buildScheduleConflictStepView(options: {
    step: ScheduleResolutionStep;
    stepIndex: number;
    stepCount: number;
    isSinglePair: boolean;
}): ScheduleConflictStepView {
    const { step, stepIndex, stepCount, isSinglePair } = options;
    const transactionCount = step.transactions.length;
    const transactionSide =
        transactionCount === 1
            ? "on the transaction"
            : `on the ${pluralizeWithCount("matched transaction", transactionCount)}`;
    const transactionNames = step.transactions.map(
        (transaction) => transaction.application.scheduleName,
    );
    const unscheduledAmount = getUnscheduledInvoiceAmount(step);
    const splitTransactionCount = step.transactions.filter(
        (transaction) => transaction.willSplitTransaction,
    ).length;

    return {
        title: isSinglePair
            ? SINGLE_PAIR_TITLE
            : `Resolve schedule conflicts (${stepIndex + 1} of ${stepCount})`,
        body: BODY,
        extra: isSinglePair ? null : OTHER_MATCHES_GO_THROUGH,
        showMatchOneAtATime: !isSinglePair && stepIndex === 0,
        options: [
            {
                kind: ScheduleMatchResolutionKind.keepInvoice,
                title: `Keep ${scheduleLabel(
                    step.invoiceApplications.map(
                        (application) => application.scheduleName,
                    ),
                )} on the invoice`,
                description: startsLabel(
                    step.invoiceApplications.map(
                        (application) => application.termStart,
                    ),
                ),
                warning: null,
                isDisabled: false,
            },
            {
                kind: ScheduleMatchResolutionKind.keepTransactions,
                title: `Keep ${scheduleLabel(transactionNames)} ${transactionSide}`,
                description: startsLabel(
                    step.transactions.map(
                        (transaction) => transaction.application.termStart,
                    ),
                ),
                warning: keepTransactionsWarning({
                    splitTransactionCount,
                    unscheduledAmount,
                }),
                isDisabled: splitTransactionCount > 0,
            },
            {
                kind: ScheduleMatchResolutionKind.unmatch,
                title: isSinglePair
                    ? "Unmatch invoice and transaction"
                    : "Don't match this invoice",
                description: UNMATCH_DESCRIPTION,
                warning: null,
                isDisabled: false,
            },
        ],
    };
}
